import { Controller } from "@hotwired/stimulus"
import ePub from "epubjs"
import { locatorForRange, rangeForLocator } from "../epub_locator.js"

const LOCATION_BREAK = 1600

export default class extends Controller {
  static targets = ["candidate", "status", "submit"]
  static values = { documentUrl: String, labels: Object }

  async connect() {
    this.submitTarget.disabled = true
    this.book = ePub(this.documentUrlValue, { openAs: "opf", replacements: "none" })

    try {
      await this.book.ready
      await Promise.all(this.candidateTargets.map((candidate) => this.resolveCandidate(candidate)))
      this.statusTarget.textContent = this.labelsValue.ready
      this.submitTarget.disabled = false
    } catch (error) {
      console.error("Unable to prepare reading migration", error)
      this.statusTarget.textContent = this.labelsValue.error
    }
  }

  disconnect() {
    this.book?.destroy()
  }

  async resolveCandidate(candidate) {
    const result = candidate.dataset.percentage
      ? await this.resolvePercentage(Number(candidate.dataset.percentage))
      : await this.resolveSemantic(candidate)
    this.writeResult(candidate, result)
  }

  async resolveSemantic(candidate) {
    const source = JSON.parse(candidate.dataset.source)
    const section = this.book.spine.get(source.href)
    if (!section) throw Object.assign(new Error("Target section is unavailable"), { code: "UNMAPPABLE_LOCATION" })

    await section.load(this.book.load.bind(this.book))
    const locator = {
      block_index: Number(candidate.dataset.blockIndex),
      start_offset: Number(candidate.dataset.startOffset),
      end_offset: Number(candidate.dataset.endOffset),
      quote: candidate.dataset.quote
    }
    const range = rangeForLocator(section.document, locator)
    if (!range) throw Object.assign(new Error("Target range is unavailable"), { code: "UNMAPPABLE_LOCATION" })

    return { location: { format: "epub", cfi: section.cfiFromRange(range) }, semanticLocator: null }
  }

  async resolvePercentage(percentage) {
    this.locations ||= await this.book.locations.generate(LOCATION_BREAK)
    const locationsBySection = this.groupLocationsBySection(this.locations)
    const sectionProgress = percentage / 100 * locationsBySection.length
    const sectionIndex = Math.min(Math.floor(sectionProgress), locationsBySection.length - 1)
    const sectionLocations = locationsBySection[sectionIndex]
    const withinSection = percentage === 100 ? 1 : sectionProgress - sectionIndex
    const locationIndex = Math.round((sectionLocations.length - 1) * withinSection)
    const cfi = sectionLocations[locationIndex]
    const range = await this.book.getRange(cfi)
    const section = this.book.spine.get(new ePub.CFI(cfi).spinePos)
    const locator = range && section && locatorForRange(section, range)
    if (!locator) throw new Error("Approximate target locator is unavailable")

    return { location: { format: "epub", cfi }, semanticLocator: locator }
  }

  groupLocationsBySection(locations) {
    return locations.reduce((sections, cfi) => {
      const spinePosition = new ePub.CFI(cfi).spinePos
      const current = sections.at(-1)
      if (current?.spinePosition === spinePosition) {
        current.locations.push(cfi)
      } else {
        sections.push({ spinePosition, locations: [cfi] })
      }
      return sections
    }, []).map((section) => section.locations)
  }

  writeResult(candidate, result) {
    const fieldPrefix = candidate.dataset.fieldPrefix
    const key = candidate.value
    const locationField = this.element.querySelector(`[data-migration-location='${CSS.escape(`${fieldPrefix}:${key}`)}']`)
    const semanticField = this.element.querySelector(`[data-migration-semantic-locator='${CSS.escape(`${fieldPrefix}:${key}`)}']`)
    locationField.value = JSON.stringify(result.location)
    if (semanticField && result.semanticLocator) semanticField.value = JSON.stringify(result.semanticLocator)
  }
}
